// disclosures.js — WP3e client (docs/frontend-validation-port-spec.md §4 WP3e).
//
// Posts live rows to the proxy's disclosures endpoint and returns the
// server-computed { perRow, summary } pair that DisclosuresPage.jsx used to
// compute locally via validation/dataDisclosures.js's detectDisclosures()/
// summarize(). Mirrors api/lifecycle.js's own request()/PROXY_API_BASE
// pattern rather than importing it — house convention duplicates this
// per-file (see api/bidderYield-adjacent clients) rather than sharing a
// helper module.
//
// Route contract (server side owned by apiServer.js, not this file):
//   POST /proxy/disclosures
//   body: { rows: DisclosureRow[] }  — DisclosureRow = the projection below
//   response: { perRow: DetectDisclosuresResult[], summary: SummarizeResult }
//     perRow is index-aligned 1:1 with the posted rows array.

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

async function request(path, init = {}) {
  let res;
  try {
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }
  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }
  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

// The only fields proxy-server/disclosures/dataDisclosures.js reads:
// row.url, row.requestBody, row.requestHeaders, row.type, row.requestType.
// Deliberately its own whitelist, NOT api/lifecycle.js's LIFECYCLE_ROW_FIELDS
// — that projection has no requestBody/requestHeaders (the lifecycle graph
// pipeline never needs raw request bodies/headers), so this ships as its own
// route + its own drift guard (api/disclosuresRowFields.test.js) rather than
// widening the shared lifecycle projection.
export const DISCLOSURES_ROW_FIELDS = ['url', 'requestBody', 'requestHeaders', 'type', 'requestType'];

export function projectRowForDisclosures(row) {
  if (!row || typeof row !== 'object') return row;
  const out = {};
  DISCLOSURES_ROW_FIELDS.forEach((f) => {
    if (f in row) out[f] = row[f];
  });
  return out;
}

/**
 * postDisclosures(rows) -> Promise<{ perRow, summary }>
 *
 * rows: the caller's full (unprojected) row array; only the whitelisted
 * fields are sent over the wire. Returns null (not throw) on a transport
 * failure or non-2xx response IS handled by the caller — this function lets
 * `request()`'s throw propagate, matching postLifecycleGroups/postVideoSessions
 * in api/lifecycle.js, which also let callers catch and fall back locally.
 */
export async function postDisclosures(rows) {
  const projected = (rows || []).map(projectRowForDisclosures);
  return request('/disclosures', {
    method: 'POST',
    body: JSON.stringify({ rows: projected }),
  });
}
